// src/features/remoteAssist/pages/RemoteAssistInboxPage.tsx

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRemoteAssistSessions, useAcceptRemoteAssistSession, useRejectRemoteAssistSession } from "../api";
import { PageBanner } from "@/shared/components/PageBanner";

export function RemoteAssistInboxPage() {
  const { data: sessions = [], isLoading } = useRemoteAssistSessions({ refetchInterval: 10000 });
  const { mutate: accept, isPending: isAccepting } = useAcceptRemoteAssistSession();
  const { mutate: reject, isPending: isRejecting } = useRejectRemoteAssistSession();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<"pending" | "active" | "all">("pending");

  const pending = sessions.filter((s) => s.status === "pending");
  const filteredSessions = sessions.filter((s) => {
    if (filter === "pending") return s.status === "pending";
    if (filter === "active") return s.status === "active";
    return true;
  });

  return (
    <div
      dir="rtl"
      style={{
        padding: "0.5rem 0",
        maxWidth: "100%",
        margin: "0",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        fontFamily: "var(--font-family-fa, sans-serif)",
      }}
    >
      <PageBanner
        title="کارتابل راهنمایی از راه دور (Remote Assist)"
        subtitle="مدیریت درخواست‌های پشتیبانی، پذیرش و کنترل مستقیم صفحه کاربران در سامانه"
        icon="🤝"
      >
        <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center" }}>
          <span
            style={{
              padding: "0.4rem 1.1rem",
              borderRadius: "12px",
              background: pending.length > 0 ? "rgba(245, 158, 11, 0.25)" : "rgba(255, 255, 255, 0.15)",
              color: "#FFFFFF",
              border: `1px solid ${pending.length > 0 ? "rgba(245, 158, 11, 0.5)" : "rgba(255, 255, 255, 0.25)"}`,
              fontWeight: 700,
              fontSize: "0.85rem",
            }}
          >
            {pending.length > 0 ? `⚠️ ${pending.length} درخواست در انتظار پذیرش` : "✓ هیچ درخواست جدیدی نیست"}
          </span>
        </div>
      </PageBanner>

      {/* FILTER TABS */}
      <div
        className="brand-card-extand"
        style={{
          padding: "0.85rem 1.25rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            type="button"
            onClick={() => setFilter("pending")}
            style={{
              padding: "6px 16px",
              borderRadius: "8px",
              border: `1px solid ${filter === "pending" ? "#007270" : "var(--color-border, #E4DCC8)"}`,
              background: filter === "pending" ? "#007270" : "#FFFFFF",
              color: filter === "pending" ? "#FFFFFF" : "#2B2B2B",
              fontSize: "0.85rem",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            در انتظار بررسی ({pending.length})
          </button>
          <button
            type="button"
            onClick={() => setFilter("active")}
            style={{
              padding: "6px 16px",
              borderRadius: "8px",
              border: `1px solid ${filter === "active" ? "#007270" : "var(--color-border, #E4DCC8)"}`,
              background: filter === "active" ? "#007270" : "#FFFFFF",
              color: filter === "active" ? "#FFFFFF" : "#2B2B2B",
              fontSize: "0.85rem",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            سشن‌های فعال ({sessions.filter((s) => s.status === "active").length})
          </button>
          <button
            type="button"
            onClick={() => setFilter("all")}
            style={{
              padding: "6px 16px",
              borderRadius: "8px",
              border: `1px solid ${filter === "all" ? "#007270" : "var(--color-border, #E4DCC8)"}`,
              background: filter === "all" ? "#007270" : "#FFFFFF",
              color: filter === "all" ? "#FFFFFF" : "#2B2B2B",
              fontSize: "0.85rem",
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
          >
            همه موارد ({sessions.length})
          </button>
        </div>

        <span style={{ fontSize: "0.82rem", color: "var(--color-text-muted, #666)" }}>
          🔄 به‌روزرسانی خودکار هر ۱۰ ثانیه
        </span>
      </div>

      {/* SESSIONS LIST */}
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {isLoading ? (
          <div
            className="brand-card-extand"
            style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted, #666)", fontWeight: 600 }}
          >
            ⏳ در حال بارگذاری درخواست‌ها...
          </div>
        ) : filteredSessions.length === 0 ? (
          <div
            className="brand-card-extand"
            style={{ padding: "3rem", textAlign: "center", color: "var(--color-text-muted, #666)", fontWeight: 600 }}
          >
            {filter === "pending"
              ? "در حال حاضر هیچ درخواست کمکی در انتظار بررسی وجود ندارد."
              : "موردی با این فیلتر یافت نشد."}
          </div>
        ) : (
          filteredSessions.map((session) => {
            const isPending = session.status === "pending";
            const isActive = session.status === "active";
            const isAccepted = session.status === "accepted";

            return (
              <div
                key={session.id}
                className="brand-card-extand"
                style={{
                  padding: "1.25rem 1.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "1rem",
                  borderLeft: isPending ? "4px solid #F59E0B" : isActive ? "4px solid #10B981" : "4px solid #9CA3AF",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
                  <span
                    style={{
                      fontSize: "1.8rem",
                      background: isPending
                        ? "rgba(245, 158, 11, 0.12)"
                        : isActive
                          ? "rgba(16, 185, 129, 0.12)"
                          : "rgba(156, 163, 175, 0.12)",
                      color: isPending ? "#B45309" : isActive ? "#10B981" : "#4B5563",
                      width: "50px",
                      height: "50px",
                      borderRadius: "12px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isPending ? "📩" : isActive ? "🟢" : "📁"}
                  </span>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                      <span style={{ fontWeight: 800, color: "#2B2B2B", fontSize: "1rem" }}>
                        کارمند: {session.employee_name || session.employee_user_id}
                      </span>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: "6px",
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          background: isPending
                            ? "rgba(245, 158, 11, 0.15)"
                            : isActive
                              ? "rgba(16, 185, 129, 0.15)"
                              : isAccepted
                                ? "rgba(2, 132, 199, 0.15)"
                                : "rgba(156, 163, 175, 0.15)",
                          color: isPending
                            ? "#B45309"
                            : isActive
                              ? "#10B981"
                              : isAccepted
                                ? "#0284C7"
                                : "#4B5563",
                        }}
                      >
                        {isPending
                          ? "در انتظار پذیرش"
                          : isActive
                            ? "اتصال فعال"
                            : isAccepted
                              ? "پذیرفته‌شده / ورود کد"
                              : session.status}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--color-text-muted, #666)" }}>
                      ثبت درخواست:{" "}
                      <span style={{ direction: "ltr", display: "inline-block", fontWeight: 600 }}>
                        {new Date(session.created_at).toLocaleString("fa-IR")}
                      </span>{" "}
                      | دامنه: {session.requester_scope === "platform_vendor" ? "پشتیبان ارائه‌دهنده" : "داخلی سازمان"}
                    </p>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  {isPending && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          accept(session.id, {
                            onSuccess: () => navigate(`/remote-assist/${session.id}`),
                          })
                        }
                        disabled={isAccepting}
                        style={{
                          padding: "8px 20px",
                          borderRadius: "10px",
                          background: "#007270",
                          color: "#FFFFFF",
                          border: "none",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          cursor: isAccepting ? "not-allowed" : "pointer",
                          boxShadow: "0 2px 8px rgba(0, 114, 112, 0.25)",
                          transition: "all 0.2s ease",
                        }}
                      >
                        {isAccepting ? "در حال اتصال..." : "پذیرش درخواست"}
                      </button>
                      <button
                        type="button"
                        onClick={() => reject(session.id)}
                        disabled={isRejecting}
                        style={{
                          padding: "8px 16px",
                          borderRadius: "10px",
                          background: "none",
                          border: "1px solid #EF4444",
                          color: "#DC2626",
                          fontSize: "0.85rem",
                          fontWeight: 700,
                          cursor: isRejecting ? "not-allowed" : "pointer",
                          transition: "background 0.2s ease",
                        }}
                      >
                        رد
                      </button>
                    </>
                  )}

                  {(isActive || isAccepted) && (
                    <button
                      type="button"
                      onClick={() => navigate(`/remote-assist/${session.id}`)}
                      style={{
                        padding: "8px 20px",
                        borderRadius: "10px",
                        background: "#0284C7",
                        color: "#FFFFFF",
                        border: "none",
                        fontSize: "0.85rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        boxShadow: "0 2px 8px rgba(2, 132, 199, 0.25)",
                      }}
                    >
                      ورود به سشن
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default RemoteAssistInboxPage;
